import { DateTimeString, toast, uuidv4 } from 'common';
import { Accessor, createEffect, createMemo, createSignal, on, Setter, Signal } from 'solid-js';
import { createStore, SetStoreFunction } from 'solid-js/store';
import { AgentConversation, AgentMessage, AgentResponseChunk, AgentSenderType, AgentTrace, supportAssistAgent as api, UserRating } from 'st-app/api';
import { showConfirmation } from '../st-confirmation-dialog/st-confirmation-dialog';

export class SuppportAssistAgentContext {
    conversations: AgentConversation[]; setConversations: SetStoreFunction<AgentConversation[]>
    conversationsLoading: Accessor<boolean>; setConversationsLoading: Setter<boolean>;
    conversationLoading: Accessor<boolean>; setConversationLoading: Setter<boolean>;

    conversationGu: Accessor<string | undefined>; setConversationGu: Setter<string | undefined>
    messages: AgentMessage[]; setMessages: SetStoreFunction<AgentMessage[]>

    referencesVisible: Accessor<boolean>; setReferencesVisible: Setter<boolean>;

    message: Accessor<string>; setMessage: Setter<string>;

    isNew: Accessor<boolean>;


    constructor() {
        [this.conversations, this.setConversations] = createStore<AgentConversation[]>([]);
        [this.conversationsLoading, this.setConversationsLoading] = createSignal<boolean>(false);
        [this.conversationLoading, this.setConversationLoading] = createSignal<boolean>(false);
        [this.conversationGu, this.setConversationGu] = createSignal<string | undefined>(undefined);
        [this.messages, this.setMessages] = createStore<AgentMessage[]>([]);
        [this.message, this.setMessage] = createSignal<string>('');
        this.isNew = createMemo(() => !this.conversations.some(c => c.aiConversationGU === this.conversationGu()));
        [this.referencesVisible, this.setReferencesVisible] = createSignal(true);

        createEffect(on(() => [this.conversationGu()], () => {
            this.loadConversation(this.conversationGu()!);
        }))
    }

    startNewConversation() {
        this.setMessages([]);
        this.setConversationGu(undefined);
    }

    async sendMessage(message: string) {
        const conversationGU = this.conversationGu() || uuidv4().toUpperCase();
        const timestamp = new Date().toISOString() as DateTimeString;

        const userMessage: AgentMessage = {
            aiMessageGU: uuidv4().toUpperCase(),
            aiConversationGU: conversationGU,
            text: message,
            timestamp: timestamp,
            sender: AgentSenderType.User,
            userRating: UserRating.None,
            traces: [],
        }

        let conversation = this.conversations.find(c => c.aiConversationGU == this.conversationGu());
        if (!conversation) {
            // Insert a new conversation if it doesn't exist
            conversation = { aiConversationGU: conversationGU, timestamp: timestamp, title: userMessage.text };
            this.setConversations([conversation, ...this.conversations]);
        }

        // Switch to the new conversation if it's not already selected
        this.setConversationGu(conversationGU);
        
        this.setMessage(''); // Clear the input message

        // Append the user message to the messages store
        this.setMessages(this.messages.length, userMessage)

        // Append a placeholder for the agent response
        this.setMessages(this.messages.length, { sender: AgentSenderType.Agent, timestamp: timestamp, traces: [] });


        try {
            for await (const chunk of api.PromptAgent(message, conversationGU)) {
                try {
                    if (chunk.payload) {
                        this.setMessages(this.messages.length - 1, 'text', chunk.payload.content || '');
                    } else if (chunk.trace) {
                        this.receiveTrace(chunk.trace);
                    }
                } catch (e) {
                    console.error('Error processing chunk:', e);
                }
            }
        } catch (error) {
            console.error('Error in streamSupportAssist:', error);
            toast.notify('error', 'Failed to stream support assist response');
            this.setMessages(m => m.slice(0, -1));
        }
    }

    receiveTrace(trace: AgentTrace) {
        const agentMessageIdx = this.messages.findLastIndex(m => m.sender === AgentSenderType.Agent)
        this.setMessages(agentMessageIdx, 'aiMessageGU', trace.aiMessageGU);
        this.setMessages(agentMessageIdx, 'aiConversationGU', trace.aiConversationGU);
        this.setMessages(agentMessageIdx, 'timestamp', trace.eventTime);
        this.setMessages(agentMessageIdx, 'traces', (traces) => [...(traces || []), trace]);
    }

    async loadConversations() {
        this.setConversationsLoading(true);
        try {
            const conversations = await api.LoadConversations();
            this.setConversations(conversations);
        } catch (error) {
            console.error('Error loading conversations:', error);
            toast.notify('error', 'Failed to load conversations');
        }
        this.setConversationsLoading(false);
    }

    async loadConversation(id: string) {
        if (this.isNew())
            return; // Don't load if we're already in a new conversation

        this.setConversationLoading(true);
        try {
            const conversation = (await api.LoadConversation(id))
                .toSorted((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            this.setMessages(conversation);
        } catch (error) {
            toast.notify('error', 'Failed to load conversation');
            console.error('Error loading conversation:', error);
        }
        this.setConversationLoading(false);
    }

    async deleteConversation(id: string) {
        try {
            await showConfirmation('Delete Conversation', 'Are you sure you want to delete this conversation? This action cannot be undone.');
        } catch (error) {
            return;
        }

        this.setConversations(this.conversations.filter(c => c.aiConversationGU !== id));
        
        await api.DeleteConversation(id);

        if (this.conversationGu() === id) {
            this.startNewConversation();
        }

    }

    async shareConversation(id: string) {
        throw new Error('Not implemented yet'); // TODO: Implement share conversation
    }

    async likeMessage(message: AgentMessage) {
        this.setMessages(m => m.aiMessageGU === message.aiMessageGU, 'userRating', UserRating.Positive);
        await api.LikeMessage(message.aiMessageGU!);
    }

    async dislikeMessage(message: AgentMessage) {
        this.setMessages(m => m.aiMessageGU === message.aiMessageGU, 'userRating', UserRating.Negative);
        await api.DislikeMessage(message.aiMessageGU!);
    }
}